/*
 * Catalog — Plane V (Part V §32–§36).
 *
 * Role: OpenAPI/AsyncAPI publication, oasdiff breaking-change detection, Pact
 * contract verification, and the public mirror.
 *
 * WIRING STATE: GET /v1/catalog/services and GET /v1/catalog/services/{id}/diff
 * are real, live reads against services/catalog_plane/read_api.py — this view
 * renders their actual data. Pact verification and the public mirror have NO
 * backend route yet; those two capabilities stay a LOUD, first-class offline
 * shell exactly as before — we NEVER fabricate a contract verdict or a mirror
 * link for a route that does not exist.
 *
 * Provability law: every live figure links to its evidence; every still-offline
 * capability's claim carries an `offline` verdict (red) plus the endpoint +
 * spec ref it WILL bind to — evidence of intent, never a fake receipt.
 */

import { useCallback, useEffect, useState } from "react";
import {
  catalog,
  isPlaneOffline,
  isMeshApiError,
  MESH_BASE,
  type PlaneOfflineError,
  type CatalogServicesResponse,
  type CatalogServiceRow,
  type CatalogDiffResult,
} from "../api/client";
import { usePlaneHealth } from "../api/metrics";
import { PlaneState } from "../components/PlaneState";
import { SpecRef } from "../components/SpecRef";
import { ProvabilityDrawer } from "../components/ProvabilityDrawer";
import { VerifyBadge } from "../components/VerifyBadge";
import type { Claim, Role } from "../api/types";

/** The public mirror — a published, read-only export of the catalogue. No
 * backend route exists yet; this stays a genuine offline shell. */
const PUBLIC_MIRROR_PATH = "/v1/catalog/mirror";

type ServicesState =
  | { kind: "loading" }
  | { kind: "offline"; offline: PlaneOfflineError }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: CatalogServicesResponse };

export default function Catalog({ role }: { role: Role }) {
  const [services, setServices] = useState<ServicesState>({ kind: "loading" });
  const [claim, setClaim] = useState<Claim | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // Live health probe of the deployed catalog-plane stub. When up, the still-
  // offline surfaces (Pact, mirror) read "deployed · health 200 · API pending".
  const { state: health } = usePlaneHealth(catalog.health);
  const healthy = health.kind === "up";
  const probing = health.kind === "probing";

  const loadServices = useCallback(() => {
    setServices({ kind: "loading" });
    catalog
      .listServices()
      .then((data) => setServices({ kind: "ready", data }))
      .catch((e) => {
        if (isPlaneOffline(e)) setServices({ kind: "offline", offline: e });
        else
          setServices({
            kind: "error",
            message: e instanceof Error ? e.message : "Unknown error",
          });
      });
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  return (
    <section>
      <header className="view-header">
        <div className="view-header__eyebrow">
          <span className="accent-indigo">Catalog</span>
          <span><SpecRef text="Plane V · §32–§36" /></span>
        </div>
        <h1 className="view-header__title">Service publication &amp; contracts</h1>
        <p className="view-header__sub">
          OpenAPI/AsyncAPI publication, oasdiff breaking-change detection, Pact
          contract verification, and the public mirror. Role:{" "}
          <strong>{role}</strong>.
        </p>
      </header>

      {/* ---- Published services (LIVE) ---- */}
      <SectionHeading
        eyebrow="Part V §32–§34 · live"
        title="Published services"
        note="Read from GET /v1/catalog/services — the mesh's OpenAPI/AsyncAPI publication list, same-origin through the console proxy."
      />

      {services.kind === "loading" && (
        <PlaneState kind="loading" message="Reading the service catalogue." />
      )}
      {services.kind === "offline" && (
        <PlaneState
          kind="offline"
          offline={services.offline}
          serviceHealthy={healthy}
          serviceProbing={probing}
          serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
          specRef="Part V §32–§34"
          endpoints={["GET /v1/catalog/services"]}
          onRetry={loadServices}
        />
      )}
      {services.kind === "error" && (
        <PlaneState
          kind="error"
          title="Unexpected catalogue response"
          message={services.message}
          specRef="Part V §32–§34"
          onRetry={loadServices}
        />
      )}
      {services.kind === "ready" && services.data.items.length === 0 && (
        <PlaneState
          kind="empty"
          title="No services published yet"
          message={`The catalogue is live and answered (generated ${services.data.generated_at}) — nothing has been published to it yet.`}
        />
      )}
      {services.kind === "ready" && services.data.items.length > 0 && (
        <div style={{ marginBottom: "var(--space-6)" }}>
          <ServiceTable
            items={services.data.items}
            generatedAt={services.data.generated_at}
            selected={selected}
            onSelect={setSelected}
            onInspect={(row) => setClaim(buildServiceClaim(row, services.data.generated_at))}
          />
        </div>
      )}

      {/* ---- Breaking-change inspector (LIVE) ---- */}
      <div style={{ height: "var(--space-6)" }} />
      <BreakingChangeInspector
        initialServiceId={selected}
        onInspect={setClaim}
      />

      {/* ---- Pact + public mirror (both genuinely offline — no route yet) ---- */}
      <div style={{ height: "var(--space-6)" }} />
      <SectionHeading
        eyebrow="Part V §36 · §34"
        title="Pact contracts & public mirror"
        note="These two capabilities have no backend read-API yet — shown honestly, never fabricated."
      />
      <div
        style={{
          display: "grid",
          gap: "var(--space-4)",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        }}
      >
        <PactCard
          healthy={healthy}
          probing={probing}
          onInspect={() => setClaim(buildPactClaim())}
        />
        <PublicMirrorCard
          healthy={healthy}
          probing={probing}
          onInspect={() => setClaim(buildMirrorClaim())}
        />
      </div>

      <ProvabilityDrawer claim={claim} onClose={() => setClaim(null)} />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Small layout helper.                                                */
/* ------------------------------------------------------------------ */

function SectionHeading({
  eyebrow,
  title,
  note,
}: {
  eyebrow: string;
  title: string;
  note: string;
}) {
  return (
    <div style={{ marginBottom: "14px" }}>
      <div
        className="mono"
        style={{
          fontSize: "11px",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--indigo)",
          marginBottom: "4px",
        }}
      >
        {eyebrow}
      </div>
      <h2 style={{ fontSize: "20px", margin: "0 0 6px" }}>{title}</h2>
      <p style={{ color: "var(--fg-faint)", margin: 0, fontSize: "13px", maxWidth: "78ch" }}>
        {note}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Published-services table — real data.                              */
/* ------------------------------------------------------------------ */

function ServiceTable({
  items,
  generatedAt,
  selected,
  onSelect,
  onInspect,
}: {
  items: CatalogServiceRow[];
  generatedAt: string;
  selected: string | null;
  onSelect: (id: string) => void;
  onInspect: (row: CatalogServiceRow) => void;
}) {
  return (
    <div className="panel" style={{ overflowX: "auto" }}>
      <div
        className="mono"
        style={{ fontSize: "11.5px", color: "var(--fg-faint)", marginBottom: "10px" }}
      >
        {items.length} published · generated {generatedAt}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "560px" }}>
        <thead>
          <tr style={{ textAlign: "left" }}>
            {["Service", "id", "version", "state", "updated"].map((h) => (
              <th
                key={h}
                className="mono"
                style={{
                  fontSize: "11px",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--fg-faint)",
                  padding: "0 10px 8px 0",
                  borderBottom: "1px solid var(--hair)",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr
              key={row.service_id}
              style={{
                borderBottom: "1px solid var(--hair)",
                background: selected === row.service_id ? "var(--indigo-dim)" : "transparent",
              }}
            >
              <td style={{ padding: "10px 10px 10px 0" }}>{row.title}</td>
              <td style={{ padding: "10px 10px 10px 0" }}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(row.service_id);
                    onInspect(row);
                  }}
                  className="mono"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    fontSize: "12.5px",
                    color: "var(--indigo)",
                    cursor: "pointer",
                  }}
                >
                  {row.service_id}
                </button>
              </td>
              <td className="mono" style={{ padding: "10px 10px 10px 0", fontSize: "12.5px" }}>
                {row.latest_version}
              </td>
              <td style={{ padding: "10px 10px 10px 0" }}>
                <VerifyBadge
                  state={row.state === "published" ? "verified" : "unknown"}
                  label={row.state}
                  size="sm"
                />
              </td>
              <td
                className="mono"
                style={{ padding: "10px 0", fontSize: "12px", color: "var(--fg-faint)" }}
              >
                {row.updated_at}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function buildServiceClaim(row: CatalogServiceRow, generatedAt: string): Claim {
  return {
    label: `${row.title} — catalogue entry`,
    verdict: row.state === "published" ? "verified" : "unknown",
    evidence: {
      hashes: [{ label: "Bundle SHA-256", value: row.bundle_sha256_hex }],
      specRef: "Part V §32–§34",
      source: "GET /v1/catalog/services",
      detail: [
        { label: "Service id", value: row.service_id },
        { label: "Country", value: row.country },
        { label: "Publisher", value: row.publisher_member_id },
        { label: "Latest version", value: row.latest_version },
        { label: "State", value: row.state },
        { label: "Updated at", value: row.updated_at },
        { label: "List generated at", value: generatedAt },
      ],
    },
  };
}

/* ------------------------------------------------------------------ */
/* Breaking-change inspector — real oasdiff classification.           */
/* ------------------------------------------------------------------ */

type DiffPhase =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "offline"; offline: PlaneOfflineError }
  | { kind: "not-found"; serviceId: string }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: CatalogDiffResult };

function BreakingChangeInspector({
  initialServiceId,
  onInspect,
}: {
  initialServiceId: string | null;
  onInspect: (c: Claim) => void;
}) {
  const [serviceId, setServiceId] = useState(initialServiceId ?? "");
  const [phase, setPhase] = useState<DiffPhase>({ kind: "idle" });

  useEffect(() => {
    if (initialServiceId) setServiceId(initialServiceId);
  }, [initialServiceId]);

  const run = useCallback(async (id: string) => {
    const trimmed = id.trim();
    if (!trimmed) return;
    setPhase({ kind: "loading" });
    try {
      const data = await catalog.getServiceDiff(trimmed);
      setPhase({ kind: "ready", data });
    } catch (e) {
      if (isPlaneOffline(e)) setPhase({ kind: "offline", offline: e });
      else if (isMeshApiError(e) && e.status === 404) {
        setPhase({ kind: "not-found", serviceId: trimmed });
      } else {
        setPhase({
          kind: "error",
          message: e instanceof Error ? e.message : "Unknown error",
        });
      }
    }
  }, []);

  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h2 style={{ fontSize: "18px" }}>Breaking-change inspector</h2>
        <p
          style={{
            color: "var(--fg-muted)",
            margin: "var(--space-1) 0 0",
            fontSize: "13.5px",
            maxWidth: "60ch",
          }}
        >
          Runs the real oasdiff classification of a service's latest version
          against its predecessor. Breaking changes render in{" "}
          <span style={{ color: "var(--red)" }}>red</span>, non-breaking in{" "}
          <span style={{ color: "var(--teal)" }}>teal</span>.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run(serviceId);
        }}
        style={{
          display: "flex",
          gap: "var(--space-3)",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", flex: "1 1 260px" }}>
          <label
            htmlFor="catalog-service-id"
            className="mono"
            style={{
              fontSize: "11px",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--fg-faint)",
            }}
          >
            Service id
          </label>
          <input
            id="catalog-service-id"
            type="text"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            placeholder="e.g. tax-clearance"
            autoComplete="off"
            spellCheck={false}
            style={{
              minHeight: "var(--tap)",
              padding: "0 var(--space-3)",
              background: "var(--bg)",
              color: "var(--fg)",
              border: "1px solid var(--hair-strong)",
              borderRadius: "var(--radius-sm)",
              fontFamily: "var(--font-mono)",
              fontSize: "13px",
            }}
          />
        </div>
        <button
          type="submit"
          disabled={phase.kind === "loading" || !serviceId.trim()}
          style={{
            minHeight: "var(--tap)",
            padding: "0 var(--space-5)",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--hair-strong)",
            background: "var(--bg)",
            color: serviceId.trim() ? "var(--fg)" : "var(--fg-faint)",
            fontFamily: "var(--font-mono)",
            fontSize: "13px",
            opacity: phase.kind === "loading" ? 0.6 : 1,
          }}
        >
          {phase.kind === "loading" ? "Requesting…" : "Run oasdiff"}
        </button>
      </form>

      {phase.kind === "idle" && (
        <div
          className="panel panel--inset"
          role="status"
          style={{
            color: "var(--fg-faint)",
            fontSize: "13px",
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "var(--fg-decor)",
              flex: "0 0 auto",
            }}
          />
          Enter a service id and run the diff. Binds to{" "}
          <span className="mono" style={{ color: "var(--fg-muted)" }}>
            GET /v1/catalog/services/&#123;id&#125;/diff
          </span>
          .
        </div>
      )}

      {phase.kind === "loading" && <PlaneState kind="loading" />}

      {phase.kind === "offline" && (
        <PlaneState
          kind="offline"
          offline={phase.offline}
          endpoints={[`GET /v1/catalog/services/${serviceId.trim() || "{id}"}/diff`]}
          specRef="Part V §35"
          onRetry={() => void run(serviceId)}
        />
      )}

      {phase.kind === "not-found" && (
        <PlaneState
          kind="empty"
          title="Unknown service id"
          message={`The catalogue answered but has no version pair on file for "${phase.serviceId}" — never a fabricated diff for an id it doesn't recognise.`}
        />
      )}

      {phase.kind === "error" && (
        <PlaneState kind="error" message={phase.message} onRetry={() => void run(serviceId)} />
      )}

      {phase.kind === "ready" && (
        <DiffResultPanel
          serviceId={serviceId.trim()}
          result={phase.data}
          onInspect={onInspect}
        />
      )}
    </div>
  );
}

function DiffResultPanel({
  serviceId,
  result,
  onInspect,
}: {
  serviceId: string;
  result: CatalogDiffResult;
  onInspect: (c: Claim) => void;
}) {
  const breaking = result.is_breaking;
  const claim: Claim = {
    label: `${serviceId} — oasdiff classification`,
    verdict: breaking ? "failed" : "verified",
    evidence: {
      specRef: "Part V §35",
      source: `GET /v1/catalog/services/${serviceId}/diff`,
      detail: [
        { label: "From version", value: result.from_version ?? "(none — first publish)" },
        { label: "To version", value: result.to_version ?? "—" },
        { label: "Verdict", value: result.verdict },
        { label: "Changes classified", value: String(result.changes.length) },
      ],
    },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <VerifyBadge
          state={breaking ? "failed" : "verified"}
          label={breaking ? "Breaking" : "Non-breaking"}
          onInspect={() => onInspect(claim)}
        />
        <span className="mono" style={{ fontSize: "12.5px", color: "var(--fg-muted)" }}>
          {result.from_version ?? "(none)"} → {result.to_version ?? "—"}
        </span>
      </div>
      {result.changes.length === 0 ? (
        <p className="mono" style={{ fontSize: "12.5px", color: "var(--fg-faint)", margin: 0 }}>
          No changes classified between these two versions.
        </p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
          {result.changes.map((c, i) => (
            <li
              key={i}
              className="mono-surface"
              style={{
                color: c.breaking ? "var(--red)" : "var(--teal)",
                display: "flex",
                gap: "var(--space-3)",
                flexWrap: "wrap",
              }}
            >
              <span>{c.breaking ? "▲ breaking" : "● non-breaking"}</span>
              <span style={{ color: "var(--fg-muted)" }}>{c.kind}</span>
              <span style={{ color: "var(--fg-faint)" }}>{c.path}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pact card — genuinely offline, no backend route yet.               */
/* ------------------------------------------------------------------ */

function buildPactClaim(): Claim {
  return {
    label: "Pact contract verification",
    verdict: "offline",
    evidence: {
      specRef: "Part V §36",
      source: `${MESH_BASE}/v1/catalog/pact/{id}`,
      detail: [
        { label: "status", value: "no backend route yet" },
        { label: "binds to", value: "GET /v1/catalog/pact/{id}" },
      ],
    },
  };
}

function PactCard({
  healthy,
  probing,
  onInspect,
}: {
  healthy: boolean;
  probing: boolean;
  onInspect: () => void;
}) {
  return (
    <article className="panel" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-3)" }}>
        <h2 style={{ fontSize: "17px" }}>Pact contract verification</h2>
        <VerifyBadge
          state={healthy ? "pending" : probing ? "unknown" : "offline"}
          label={healthy ? "API pending" : probing ? "Checking…" : undefined}
          size="sm"
          onInspect={onInspect}
        />
      </div>
      <p style={{ color: "var(--fg-muted)", margin: 0, fontSize: "13.5px", maxWidth: "48ch" }}>
        Consumer-driven contract state: which provider versions satisfy which
        consumer expectations, and the last verification result.
      </p>
      <div className="mono-surface" style={{ color: "var(--fg-muted)" }}>
        GET /v1/catalog/pact/&#123;id&#125;
      </div>
      <span
        className="mono"
        style={{ fontSize: "11.5px", color: healthy ? "var(--indigo)" : "var(--fg-faint)" }}
      >
        {healthy ? "catalog-pact deployed · API pending" : probing ? "checking catalog-pact health…" : "awaiting catalog-pact"}
      </span>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Public mirror card — genuinely offline, no backend route yet.      */
/* ------------------------------------------------------------------ */

function buildMirrorClaim(): Claim {
  return {
    label: "Public catalogue mirror",
    verdict: "offline",
    evidence: {
      specRef: "Part V §34",
      source: `${MESH_BASE}${PUBLIC_MIRROR_PATH}`,
      detail: [
        { label: "binds to", value: `GET ${PUBLIC_MIRROR_PATH}` },
        {
          label: "note",
          value:
            "Read-only, signed export. No mirror is shown until the route is served — we do not link a mirror we cannot serve.",
        },
      ],
    },
  };
}

function PublicMirrorCard({
  healthy,
  probing,
  onInspect,
}: {
  healthy: boolean;
  probing: boolean;
  onInspect: () => void;
}) {
  return (
    <article className="panel" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-3)" }}>
        <h2 style={{ fontSize: "17px" }}>Public mirror</h2>
        <VerifyBadge
          state={healthy ? "pending" : probing ? "unknown" : "offline"}
          label={healthy ? "API pending" : probing ? "Checking…" : undefined}
          size="sm"
          onInspect={onInspect}
        />
      </div>
      <p style={{ color: "var(--fg-muted)", margin: 0, fontSize: "13.5px", maxWidth: "48ch" }}>
        A read-only, signed export of the published catalogue for external
        consumers. The console links the mirror only once it can serve it.
      </p>
      <div className="mono-surface" style={{ color: "var(--fg-faint)" }}>
        {MESH_BASE}
        {PUBLIC_MIRROR_PATH}{" "}
        {healthy ? (
          <span style={{ color: "var(--indigo)" }}>· not yet published</span>
        ) : probing ? (
          <span style={{ color: "var(--fg-faint)" }}>· checking…</span>
        ) : (
          <span style={{ color: "var(--red)" }}>· unavailable</span>
        )}
      </div>
    </article>
  );
}
